/**
 * src/api/customerApi.ts — Customer-domain helpers.
 *
 * Thin wrappers over atticApi.updateMyProfile → Harper's /Me Resource
 * (PATCH). Exposes a typed success/error result so callers don't need
 * try/catch.
 */

import { updateMyProfile } from "./atticApi";
import type { CustomerRole } from "../types";

/** Payload for the profile save mutation (AC-003). */
export interface UpdateCustomerProfilePayload {
  name: string;
  address: string;
  phone: string;
  /** Customer's standing pickup handoff preference. */
  default_pickup_method?: 'porch_pickup' | 'doorbell';
  /** JSON-serialized structured address components. */
  address_components?: string;
  /** Standing gate / building access code (driver-visible on every stop). */
  gate_code?: string;
  /** Standing buzzer / apartment label. */
  buzzer_apartment?: string;
  /** Standing free-form instructions for the driver. */
  special_instructions?: string;
  /** Address environment — drives planner service-time matrix. */
  location_type?:
    | 'sfh_ground'
    | 'sfh_stairs'
    | 'townhome'
    | 'apt_elevator'
    | 'apt_walkup'
    | 'downtown'
    | 'other';
  /** Floor number for apt_elevator / apt_walkup. */
  floor_number?: number | null;
}

/** Discriminated union result — avoids throwing across the hook boundary. */
export type UpdateCustomerProfileResult =
  | { success: true }
  | { success: false; error: string };

/**
 * updateCustomerProfile — sends a Customer update via the Express API.
 *
 * - Server injects updated_at timestamp.
 * - Returns a typed success/error discriminated union.
 * - Does NOT throw; callers receive an error result instead of an exception.
 *
 * @param _customerId  Ignored — server scopes to authenticated user via session cookie.
 * @param payload      New name, address, phone values.
 */
export async function updateCustomerProfile(
  _customerId: string,
  payload: UpdateCustomerProfilePayload,
): Promise<UpdateCustomerProfileResult> {
  try {
    const fields: Record<string, unknown> = {
      name: payload.name.trim(),
      address: payload.address.trim(),
      phone: payload.phone.trim(),
    };
    if (payload.address_components !== undefined) fields.address_components = payload.address_components;
    if (payload.location_type !== undefined) fields.location_type = payload.location_type;
    if (payload.floor_number !== undefined) fields.floor_number = payload.floor_number;
    if (payload.default_pickup_method !== undefined) fields.default_pickup_method = payload.default_pickup_method;
    await updateMyProfile(fields);
    return { success: true };
  } catch (err) {
    const message = err instanceof Error ? err.message : "Unknown error";
    return { success: false, error: message };
  }
}

/**
 * updateCustomerRole — writes a CustomerRole value via the Express API.
 *
 * Used by DevRoleSwitcher (WP-ATTICDEV-031-ROLE AC-003).
 *
 * - Server scopes to authenticated user via session cookie (RBAC).
 * - Returns a typed success/error discriminated union — does NOT throw.
 *
 * @param _customerId  Ignored — server scopes to authenticated user via session cookie.
 * @param role         New CustomerRole value ('customer' | 'driver' | 'both').
 */
export async function updateCustomerRole(
  _customerId: string,
  role: CustomerRole,
): Promise<UpdateCustomerProfileResult> {
  try {
    await updateMyProfile({ role });
    return { success: true };
  } catch (err) {
    const message = err instanceof Error ? err.message : "Unknown error";
    return { success: false, error: message };
  }
}
